/*
=========================================================
* Material Kit 2 React - v2.1.0
=========================================================

* Product Page: https://www.creative-tim.com/product/material-kit-react
* Copyright 2023 Creative Tim (https://www.creative-tim.com)

Coded by www.creative-tim.com

 =========================================================

* The above copyright notice and this permission notice shall be included in all copies or substantial portions of the Software.
*/

// @mui material components
import Container from "@mui/material/Container";
import Grid from "@mui/material/Grid";
import Card from "@mui/material/Card";

// Material Kit 2 React components
import MKBox from "components/MKBox";
import MKTypography from "components/MKTypography";
import MKSocialButton from "components/MKSocialButton";

// Material Kit 2 React examples
import DefaultNavbar from "examples/Navbars/DefaultNavbar";
import DefaultFooter from "examples/Footers/DefaultFooter";
import FilledInfoCard from "examples/Cards/InfoCards/FilledInfoCard";

// Presentation page sections
import Counters from "pages/Presentation/sections/Counters";
import Information from "pages/Presentation/sections/Information";
import DesignBlocks from "pages/Presentation/sections/DesignBlocks";
import Pages from "pages/Presentation/sections/Pages";
import Testimonials from "pages/Presentation/sections/Testimonials";
import Download from "pages/Presentation/sections/Download";

// Presentation page components
import BuiltByDevelopers from "pages/Presentation/components/BuiltByDevelopers";

// Routes
import routes from "routes";
import footerRoutes from "footer.routes";

// Images
import bgImage from "assets/images/bg-presentation.jpg";
import knImage from "assets/images/kn-logo.png";
import { Opacity } from "@mui/icons-material";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faWhatsapp } from "@fortawesome/free-brands-svg-icons";
import { useState } from "react";
import "./index.css";

export const WhatsappButton = () => {
  const whatsappLink = `https://wa.me/573183759561?text=${encodeURIComponent(
    "¡Hola!, Somos Kinitech SAS. Estamos aquí para ayudarte con soluciones tecnológicas innovadoras y seguras. ¿Cómo podemos asistirte hoy?"
  )}`;

  return (
    <a
      href={whatsappLink}
      target="_blank"
      rel="noopener noreferrer"
      style={{
        position: "fixed",
        bottom: "20px",
        right: "50px",
        backgroundColor: "#25D366",
        color: "white",
        borderRadius: "50%",
        width: "60px",
        height: "60px",
        display: "flex",
        justifyContent: "center",
        alignItems: "center",
        boxShadow: "0 4px 8px rgba(0, 0, 0, 0.3)",
        textDecoration: "none",
        zIndex: 1000,
      }}
    >
      <FontAwesomeIcon icon={faWhatsapp} size="2x" />
    </a>
  );
};

function Presentation() {
  const [isVideoLoaded, setIsVideoLoaded] = useState(false); // Estado para controlar la carga del video

  const handleVideoLoad = () => {
    // Agrega un retraso antes de ocultar la imagen de carga
    setTimeout(() => {
      setIsVideoLoaded(true);
    }, 1800); // Retraso de 500ms (puedes ajustarlo según sea necesario)
  };
  return (
    <>
      <DefaultNavbar
        routes={routes}
        action={{
          type: "internal",
          route: "/pages/landing-pages/about-us",
          label: "Contactenos",
          color: "info",
        }}
        sticky
        brand="KINITECH"
      />
      <MKBox
        minHeight={{ xs: "50vh", lg: "80vh" }} // Ocupa toda la altura de la pantalla
        width="100%" // Ocupa todo el ancho de la pantalla
        sx={{
          position: "relative",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          containerType: "size",
          overflow: "hidden", // Asegura que no haya desbordamiento
        }}
      >
        {/* Imagen de carga */}
        {!isVideoLoaded && (
          <img
            src={bgImage} // Cambia esto por la URL de tu imagen de carga
            alt="Cargando..."
            style={{
              position: "absolute",
              top: 0,
              left: 0,
              width: "100%",
              height: "100%",
              objectFit: "cover", // Asegura que la imagen cubra todo el contenedor
              zIndex: -1,
              opacity: 0.8, // Controla la transparencia de la imagen (0 es completamente transparente, 1 es opaco)
              backgroundColor: "rgba(0, 0, 0, 0.5)",
            }}
          />
        )}

        {/* Video */}
        <iframe
          src="https://player.vimeo.com/video/1074143818?h=8b0fb34cb5&title=0&byline=0&portrait=0&badge=0&autopause=0&loop=1&background=1&responsive=1"
          title="video Kinitech"
          frameBorder="0"
          allow="autoplay; fullscreen"
          allowFullScreen
          onLoad={handleVideoLoad} // Cambia el estado cuando el video se carga
          className="video-responsive"
        ></iframe>
        <div className="video-whitewash" />

        <Container sx={{ padding: "10px" }}>
          <Grid container item xs={12} lg={7} justifyContent="center" mx="auto">
            <MKTypography
              variant="h1"
              color="white"
              mb={1}
              sx={({ breakpoints, typography: { size } }) => ({
                [breakpoints.down("md")]: {
                  fontSize: size["3xl"],
                },
              })}
            >
              {" "}
              <img
                src={knImage}
                alt="Kinitech video"
                className="hero-logo-shadow"
                style={{ zoom: 1.2 }}
              />
            </MKTypography>
            <MKTypography
              variant="body1"
              color="#344767"
              textAlign="center"
              px={{ xs: 1, lg: 2 }}
              mt={1}
              fontSize={{ xs: 18, lg: 32 }}
              fontWeight={"bold"}
            >
              <div
                className="hero-title-shadow"
                style={{ maxWidth: "80vw", margin: "0px auto" }}
              >
                KINITECH, tu aliado en Cali: outsourcing de impresión y
                suministros HP originales para empresas.
              </div>
            </MKTypography>
          </Grid>
        </Container>
      </MKBox>
      <Card
        sx={{
          p: 2,
          mx: { xs: 2, lg: 3 },
          mt: -8,
          mb: 4,
          backgroundColor: ({ palette: { white }, functions: { rgba } }) =>
            rgba(white.main, 0.8),
          backdropFilter: "saturate(200%) blur(30px)",
          boxShadow: ({ boxShadows: { xxl } }) => xxl,
        }}
      >
        <Counters />

        <Information />
        <DesignBlocks />

        <Testimonials />

        {/* <MKBox pt={18} pb={6}>
          <Container>
            <Grid container spacing={3}>
              <Grid item xs={12} lg={5} ml="auto" sx={{ textAlign: { xs: "center", lg: "left" } }}>
                <MKTypography variant="h4" fontWeight="bold" mb={0.5}>
                  Siguenos en nuestras redes!
                </MKTypography>
                <MKTypography variant="body1" color="text">
                  We deliver the best web products
                </MKTypography>
              </Grid>
              <Grid
                item
                xs={12}
                lg={5}
                my={{ xs: 5, lg: "auto" }}
                mr={{ xs: 0, lg: "auto" }}
                sx={{ textAlign: { xs: "center", lg: "right" } }}
              >
                <MKSocialButton
                  component="a"
                  href="https://twitter.com/intent/tweet?text=Check%20Material%20Design%20System%20made%20by%20%40CreativeTim%20%23webdesign%20%23designsystem%20%23mui5&amp;url=https%3A%2F%2Fwww.creative-tim.com%2Fproduct%2Fmaterial-kit-react"
                  target="_blank"
                  color="twitter"
                  sx={{ mr: 1 }}
                >
                  <i className="fab fa-twitter" />
                  &nbsp;Tweet
                </MKSocialButton>
                <MKSocialButton
                  component="a"
                  href="https://www.facebook.com/sharer/sharer.php?u=https://www.creative-tim.com/product/material-kit-react"
                  target="_blank"
                  color="facebook"
                  sx={{ mr: 1 }}
                >
                  <i className="fab fa-facebook" />
                  &nbsp;Share
                </MKSocialButton>
                <MKSocialButton
                  component="a"
                  href="https://www.pinterest.com/pin/create/button/?url=https://www.creative-tim.com/product/material-kit-react"
                  target="_blank"
                  color="pinterest"
                >
                  <i className="fab fa-pinterest" />
                  &nbsp;Pin it
                </MKSocialButton>
              </Grid>
            </Grid>
          </Container>
        </MKBox> */}
      </Card>

      <MKBox pt={6} px={1} mt={6}>
        <DefaultFooter content={footerRoutes} />
      </MKBox>
    </>
  );
}

export default Presentation;
